import PropTypes from 'prop-types';
import { tourType } from '../../../util/sharedPropTypes';

const propTypes = {
  label: PropTypes.string.isRequired,
  labels: PropTypes.arrayOf(PropTypes.string).isRequired,
  packages: PropTypes.arrayOf(tourType).isRequired,
};

/**
 * Returns the maximum length and maximum upgrades length for a package comparison tour item
 * Also determines whether to show or a row or not based on all item values in row.
 *
 * @param {string} label - The label for the tour item which the maximum lengths are required for
 * @param {array} labels - All of the labels within the package comparison modal
 * @param {array} packages - The tour packages which are being compared
 * @returns {Object} maxLengths
 *  * {string} maxLength - The maximum possible length of the tour item
 *  * {string} maxUpgradesLength - The maximum possible length of the tour items upgrades
 */

function getMaximumLengths(label, labels, packages) {
  let maxLength = 0;
  let maxUpgradesLength = 0;

  switch (label) {
    // Hotels
    case labels?.hotelLineItemText: {
      if (!labels?.hotelLineItemText) break;

      let maxHotels = 0;
      let maxHotelUpgrades = 0;

      packages.forEach((tourPackage) => {
        const hotels = tourPackage?.accommodationOptions?.filter(
          (option) => option.accommodationType === 'hotel'
        );

        hotels.forEach((hotel) => {
          if (hotel?.upgrades) {
            maxHotelUpgrades = Math.max(maxHotelUpgrades, hotel.upgrades.length);
          }
        });

        maxHotels = Math.max(maxHotels, hotels.length);
      });

      maxLength = maxHotels;
      maxUpgradesLength = maxHotelUpgrades;
      break;
    }

    // Motorhome
    case labels?.motorhomeLineItemText: {
      if (!labels?.motorhomeLineItemText) break;

      let maxMotorhomes = 0;
      let maxMotorhomeUpgrades = 0;

      packages.forEach((tourPackage) => {
        const motorhomes = tourPackage?.accommodationOptions?.filter(
          (option) => option.accommodationType === 'motorhome'
        );

        motorhomes.forEach((motorhome) => {
          if (motorhome?.upgrades) {
            maxMotorhomeUpgrades = Math.max(maxMotorhomeUpgrades, motorhome.upgrades.length);
          }
        });

        maxMotorhomes = Math.max(maxMotorhomes, motorhomes.length);
      });

      maxLength = maxMotorhomes;
      maxUpgradesLength = maxMotorhomeUpgrades;
      break;
    }

    // Camping
    case labels?.campingLineItemText || 'Camping': {
      if (!labels?.campingLineItemText && label !== 'Camping') break;

      let maxCamping = 0;
      let maxCampingUpgrades = 0;

      packages.forEach((tourPackage) => {
        const campingOptions = tourPackage?.campingOptions || [];

        campingOptions.forEach((camping) => {
          if (camping?.upgrades) {
            maxCampingUpgrades = Math.max(maxCampingUpgrades, camping.upgrades.length);
          }
        });

        maxCamping = Math.max(maxCamping, campingOptions.length);
      });

      maxLength = maxCamping;
      maxUpgradesLength = maxCampingUpgrades;
      break;
    }

    // Flights
    case labels?.flightLineItemText: {
      if (!labels?.flightLineItemText) break;

      let hasDefaultFlight = false;

      packages.forEach((tourPackage) => {
        if (tourPackage?.flightOptions?.[0]?.defaultOption?.option) {
          hasDefaultFlight = true;
        }
      });

      maxLength = hasDefaultFlight ? 1 : 0; // Only 1 row shown for flights
      maxUpgradesLength = 0; // No flight upgrades, these are shown under Cabin Class

      break;
    }

    // Flight Cabin Class
    case labels?.flightClassLineItemText: {
      if (!labels?.flightClassLineItemText) break;

      let hasCabinClass = false;
      let maxFlightUpgradesLength = 0; // Flight upgrades are displayed in the cabin class row

      packages.forEach((tourPackage) => {
        if (tourPackage?.flightOptions?.[0]?.upgrades) {
          maxFlightUpgradesLength = Math.max(
            maxFlightUpgradesLength,
            tourPackage?.flightOptions?.[0]?.upgrades?.length
          );
        }
        if (tourPackage.flightOptions?.[0]?.defaultOption?.flightClasses?.[0]?.option.flightClass) {
          hasCabinClass = true;
        }
      });

      maxLength = hasCabinClass ? 1 : 0; // only 1 row is shown for cabin class
      maxUpgradesLength = maxFlightUpgradesLength;
      break;
    }

    // Inclusions
    case labels?.extraLineItemText: {
      if (!labels?.extraLineItemText) break;

      let maxInclusions = 0;

      packages.forEach((tourPackage) => {
        maxInclusions = Math.max(maxInclusions, tourPackage?.extraOptions?.length);
      });

      maxLength = maxInclusions;
      break;
    }

    // Tickets
    case labels?.ticketLineItemText: {
      if (!labels?.ticketLineItemText) break;

      let hasTickets = false;
      let maxTicketUpgrades = 0;

      packages.forEach((tourPackage) => {
        if (tourPackage.ticketOptions?.[0]?.defaultOption?.option) {
          hasTickets = true;
        }
        if (tourPackage?.ticketOptions?.[0]?.upgrades) {
          maxTicketUpgrades = Math.max(
            maxTicketUpgrades,
            tourPackage?.ticketOptions?.[0]?.upgrades.length
          );
        }
      });

      maxLength = hasTickets ? 1 : 0; // only 1 row is shown for tickets
      maxUpgradesLength = maxTicketUpgrades;
      break;
    }

    // Extras (not included in the price)
    case labels?.excursionLineItemText: {
      if (!labels?.excursionLineItemText) break;

      let maxExtras = 0;

      packages.forEach((tourPackage) => {
        maxExtras = Math.max(maxExtras, tourPackage?.excursionOptions?.length);
      });

      maxLength = maxExtras;
      break;
    }

    // Stopovers
    case labels?.stopoverLineItemText: {
      const hasStopoverInAnyTour = packages.some((tour) => tour.hasStopovers);
      return { maxLength: hasStopoverInAnyTour ? 1 : 0 };
    }

    // Merchandise
    case labels?.merchandiseLineItemText: {
      const hasMerchandiseInAnyTour = packages.some((tour) => tour.hasMerchandise);
      return { maxLength: hasMerchandiseInAnyTour ? 1 : 0 };
    }

    default:
      maxLength = 1; // any other labels will be a single row
      maxUpgradesLength = 0;
  }

  return { maxLength, maxUpgradesLength };
}

getMaximumLengths.propTypes = propTypes;
export default getMaximumLengths;
